import type { MonthlyDemandRow } from "../types";

export type TokenDemandScope = "industry-demand-proxy" | "single-platform";

export interface TokenDemandOptions {
  /** Point-in-time cutoff. Rows from its UTC calendar month are incomplete. */
  asOf: string;
  scenario?: {
    demandGrowthPct?: number;
    unitPriceChangePct?: number;
    priceElasticity?: number;
  };
}

export interface TokenDemandMonitorPoint {
  month: string;
  value: number;
}

export interface TokenDemandMonitorSeries {
  sourceId: "npm-wikimedia" | "openrouter";
  label: string;
  scope: TokenDemandScope;
  unit: "index" | "trillion-platform-tokens";
  latestMonth: string | null;
  latestValue: number | null;
  points: TokenDemandMonitorPoint[];
  warning: string;
}

export interface TokenDemandSourceHealth {
  sourceId: "npm" | "wikimedia" | "openrouter" | "npm-wikimedia";
  watermark: string | null;
  coverage: {
    firstMonth: string | null;
    lastMonth: string | null;
    observedMonths: number;
    expectedMonths: number;
  };
  missingMonths: string[];
  freshnessDays: number | null;
  status: "current" | "stale" | "degraded" | "unavailable";
}

export type TokenDemandIntervalSource = "oos-conformal" | "in-sample-rmse-fallback";

export interface TokenDemandInterval {
  /** A statistical level is exposed only for genuinely calibrated OOS conformal intervals. */
  level: 0.9 | null;
  source: TokenDemandIntervalSource;
  residualCount: number;
  calibrated: boolean;
  label: string;
}

export interface TokenDemandForecastPoint {
  month: string;
  horizon: number;
  predicted: number;
  low: number;
  high: number;
  interval: TokenDemandInterval;
}

export interface TokenDemandBacktestPoint extends TokenDemandForecastPoint {
  actual: number;
  error: number;
  absoluteError: number;
  absolutePercentageError: number;
  intervalCovered: boolean;
}

export interface TokenDemandMetricWindow {
  observations: number;
  mae: number;
  rmse: number;
  mape: number;
  meanError: number;
}

export interface TokenDemandBacktestModel {
  id: "adaptive-selector" | "lag-1" | "seasonal-lag-12" | "drift";
  label: string;
  points: TokenDemandBacktestPoint[];
  metrics: {
    full: TokenDemandMetricWindow;
    recent6: TokenDemandMetricWindow;
    recent12: TokenDemandMetricWindow;
  };
}

export interface TokenDemandMethodology {
  targetDefinition: string;
  formula: string;
  estimator: string;
  featureTiming: string;
  validation: string;
  interval: string;
  scenarioTransform: string;
  minimumTrainingMonths: number;
  caveats: string[];
}

export interface TokenDemandSliceResult {
  monitor: {
    asOf: string;
    latestCompleteMonth: string;
    industryDemandProxy: TokenDemandMonitorSeries;
    platformTokenActual: TokenDemandMonitorSeries;
    modelProviderMomentum: {
      available: false;
      label: "platform-share-momentum-index";
      reason: string;
    };
    sourceHealth: {
      npm: TokenDemandSourceHealth;
      wikimedia: TokenDemandSourceHealth;
      demandProxy: TokenDemandSourceHealth;
      openrouter: TokenDemandSourceHealth;
    };
  };
  forecast: {
    target: "monthly-demand-index";
    unit: "index";
    baselinePoints: TokenDemandForecastPoint[];
    points: TokenDemandForecastPoint[];
    horizons: {
      month1: TokenDemandForecastPoint;
      month3: TokenDemandForecastPoint;
      month12: TokenDemandForecastPoint;
    };
    openRouterChallenger: {
      role: "restricted-challenger-only" | "eligible-challenger-only";
      observedMonths: number;
      minimumMonths: 18;
      appliedToPrimary: false;
      explanation: string;
    };
    scenarioImpact: {
      affectsCurrentChart: boolean;
      directTargets: ["monthly-demand-index"];
      historicalBacktestLocked: true;
      explanation: string;
    };
  };
  backtest: {
    primary: TokenDemandBacktestModel;
    baselines: {
      lag1: TokenDemandBacktestModel;
      seasonalLag12: TokenDemandBacktestModel;
      drift: TokenDemandBacktestModel;
    };
    skillVsBaselinesPct: {
      lag1: number | null;
      seasonalLag12: number | null;
      drift: number | null;
    };
    historicalScenarioLocked: true;
  };
  methodology: TokenDemandMethodology;
  sources: Array<{
    id: "npm" | "wikimedia" | "openrouter";
    role: string;
    scope: string;
  }>;
}

function parseAsOf(asOf: string) {
  const parsed = new Date(asOf.length === 10 ? `${asOf}T00:00:00.000Z` : asOf);
  if (!Number.isFinite(parsed.getTime())) throw new Error(`Invalid asOf timestamp: ${asOf}`);
  return parsed;
}

function calendarMonth(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function previousMonth(month: string) {
  const [year, value] = month.split("-").map(Number);
  return calendarMonth(new Date(Date.UTC(year, value - 2, 1)));
}

function nextMonth(month: string) {
  const [year, value] = month.split("-").map(Number);
  return calendarMonth(new Date(Date.UTC(year, value, 1)));
}

function addMonths(month: string, amount: number) {
  const [year, value] = month.split("-").map(Number);
  return calendarMonth(new Date(Date.UTC(year, value - 1 + amount, 1)));
}

function monthsBetween(start: string, end: string) {
  const [startYear, startMonth] = start.split("-").map(Number);
  const [endYear, endMonth] = end.split("-").map(Number);
  return (endYear - startYear) * 12 + endMonth - startMonth;
}

function monthRange(start: string, end: string) {
  const months: string[] = [];
  for (let month = start; month <= end; month = nextMonth(month)) months.push(month);
  return months;
}

function endOfMonth(month: string) {
  const [year, value] = month.split("-").map(Number);
  return new Date(Date.UTC(year, value, 0, 23, 59, 59, 999));
}

function sourceHealth(
  sourceId: TokenDemandSourceHealth["sourceId"],
  rows: readonly MonthlyDemandRow[],
  latestCompleteMonth: string,
  asOfDate: Date,
  observed: (row: MonthlyDemandRow) => boolean,
): TokenDemandSourceHealth {
  const months = rows.filter(observed).map((row) => row.month);
  const firstMonth = months.at(0) ?? null;
  const lastMonth = months.at(-1) ?? null;
  if (!firstMonth || !lastMonth) {
    return {
      sourceId,
      watermark: null,
      coverage: { firstMonth: null, lastMonth: null, observedMonths: 0, expectedMonths: 0 },
      missingMonths: [],
      freshnessDays: null,
      status: "unavailable",
    };
  }
  const expected = monthRange(firstMonth, latestCompleteMonth);
  const observedSet = new Set(months);
  const missingMonths = expected.filter((month) => !observedSet.has(month));
  const internalMissing = missingMonths.filter((month) => month <= lastMonth);
  const watermarkDate = endOfMonth(lastMonth);
  const freshnessDays = Math.max(0, Math.floor((asOfDate.getTime() - watermarkDate.getTime()) / 86_400_000));
  return {
    sourceId,
    watermark: watermarkDate.toISOString(),
    coverage: {
      firstMonth,
      lastMonth,
      observedMonths: months.length,
      expectedMonths: expected.length,
    },
    missingMonths,
    freshnessDays,
    status: internalMissing.length ? "degraded" : lastMonth === latestCompleteMonth ? "current" : "stale",
  };
}

function requiredLatest<T>(points: T[], label: string): T {
  const latest = points.at(-1);
  if (!latest) throw new Error(`${label} has no complete monthly observations.`);
  return latest;
}

function isPresent(value: unknown): value is number {
  return value !== null && value !== undefined;
}

function validateMonthlyRows(rows: readonly MonthlyDemandRow[]) {
  let previous: string | null = null;
  const seen = new Set<string>();
  rows.forEach((row, index) => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(row.month)) {
      throw new Error(`Row ${index + 1} has invalid calendar month: ${row.month}`);
    }
    if (seen.has(row.month)) throw new Error(`Duplicate month ${row.month}.`);
    if (previous && row.month <= previous) {
      throw new Error(`Monthly rows must be strictly ascending: ${previous} then ${row.month}.`);
    }
    seen.add(row.month);
    previous = row.month;

    const numericFields = [
      ["sdkDownloads", row.sdkDownloads, true],
      ["wikiViews", row.wikiViews, true],
      ["demandIndex", row.demandIndex, false],
      ["openRouterTokensT", row.openRouterTokensT, false],
      ["openRouterTokenIndex", row.openRouterTokenIndex, false],
    ] as const;
    for (const [field, value, allowZero] of numericFields) {
      if (!isPresent(value)) continue;
      if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new Error(`${row.month} ${field} must be finite or null.`);
      }
      if (allowZero ? value < 0 : value <= 0) {
        throw new Error(`${row.month} ${field} must be ${allowZero ? "non-negative" : "greater than zero"}.`);
      }
    }
    const hasRouterTokens = isPresent(row.openRouterTokensT);
    const hasRouterIndex = isPresent(row.openRouterTokenIndex);
    if (hasRouterTokens !== hasRouterIndex) {
      throw new Error(`${row.month} OpenRouter tokens and index must be provided as a pair.`);
    }
  });
}

interface DemandObservation {
  month: string;
  value: number;
}

interface CandidatePrediction {
  lag1: number;
  seasonalLag12: number | null;
  drift: number;
}

type BaselineKey = "lag1" | "seasonalLag12" | "drift";

function median(values: number[]) {
  const ordered = [...values].sort((left, right) => left - right);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2 ? ordered[middle] : (ordered[middle - 1] + ordered[middle]) / 2;
}

function predictCandidates(history: readonly DemandObservation[], targetMonth: string): CandidatePrediction {
  const first = history[0];
  const last = history.at(-1);
  if (!first || !last) throw new Error(`Cannot forecast ${targetMonth} without monthly demand history.`);
  const horizon = monthsBetween(last.month, targetMonth);
  if (horizon <= 0) throw new Error(`Forecast target ${targetMonth} must follow origin ${last.month}.`);
  const elapsed = monthsBetween(first.month, last.month);
  if (elapsed <= 0) throw new Error(`Drift model needs at least two distinct calendar months before ${targetMonth}.`);
  const monthlyGrowth = Math.pow(last.value / first.value, 1 / elapsed);
  if (!Number.isFinite(monthlyGrowth) || monthlyGrowth <= 0) {
    throw new Error(`Cannot estimate positive log drift at ${last.month}.`);
  }
  const drift = last.value * Math.pow(monthlyGrowth, horizon);
  const seasonalLag12 = history.find((row) => row.month === addMonths(targetMonth, -12))?.value ?? null;
  return {
    lag1: last.value,
    seasonalLag12,
    drift,
  };
}

function recentCandidateRmse(points: readonly TokenDemandBacktestPoint[]) {
  const recent = points.slice(-12);
  return recent.length
    ? Math.sqrt(recent.reduce((sum, point) => sum + point.error * point.error, 0) / recent.length)
    : Number.POSITIVE_INFINITY;
}

function adaptivePrediction(
  candidates: CandidatePrediction,
  prior: Record<BaselineKey, readonly TokenDemandBacktestPoint[]>,
) {
  const available: Array<{ key: BaselineKey; value: number }> = [
    { key: "lag1", value: candidates.lag1 },
    ...(candidates.seasonalLag12 === null ? [] : [{ key: "seasonalLag12" as const, value: candidates.seasonalLag12 }]),
    { key: "drift", value: candidates.drift },
  ];
  if (available.every((candidate) => prior[candidate.key].length >= 6)) {
    return available
      .map((candidate) => ({ ...candidate, rmse: recentCandidateRmse(prior[candidate.key]) }))
      .sort((left, right) => left.rmse - right.rmse || left.key.localeCompare(right.key))[0].value;
  }
  return median(available.map((candidate) => candidate.value));
}

function quantile90(values: readonly number[]) {
  const sorted = [...values].sort((left, right) => left - right);
  const index = Math.min(sorted.length - 1, Math.ceil((sorted.length + 1) * 0.9) - 1);
  return sorted[index];
}

function fallbackRadius(history: readonly DemandObservation[], horizon: number) {
  const changes = history.slice(1).flatMap((row, index) =>
    monthsBetween(history[index].month, row.month) === 1 ? [row.value - history[index].value] : [],
  );
  const last = history.at(-1)!;
  const rmse = changes.length
    ? Math.sqrt(changes.reduce((sum, value) => sum + value * value, 0) / changes.length)
    : last.value * 0.05;
  return Math.max(last.value * 0.05, 1.645 * rmse) * Math.sqrt(horizon);
}

function intervalFor(
  predicted: number,
  residuals: readonly number[],
  history: readonly DemandObservation[],
  horizon: number,
): Pick<TokenDemandForecastPoint, "low" | "high" | "interval"> {
  const calibrated = residuals.length >= 9;
  const radius = calibrated ? quantile90(residuals) : fallbackRadius(history, horizon);
  return {
    low: Math.max(0, predicted - radius),
    high: predicted + radius,
    interval: calibrated
      ? {
          level: 0.9,
          source: "oos-conformal",
          residualCount: residuals.length,
          calibrated: true,
          label: "90% OOS conformal prediction interval",
        }
      : {
          level: null,
          source: "in-sample-rmse-fallback",
          residualCount: residuals.length,
          calibrated: false,
          label: "Uncalibrated RMSE fallback band",
        },
  };
}

function metricWindow(points: readonly TokenDemandBacktestPoint[]): TokenDemandMetricWindow {
  if (!points.length) return { observations: 0, mae: 0, rmse: 0, mape: 0, meanError: 0 };
  return {
    observations: points.length,
    mae: points.reduce((sum, point) => sum + point.absoluteError, 0) / points.length,
    rmse: Math.sqrt(points.reduce((sum, point) => sum + point.error * point.error, 0) / points.length),
    mape: points.reduce((sum, point) => sum + point.absolutePercentageError, 0) / points.length,
    meanError: points.reduce((sum, point) => sum + point.error, 0) / points.length,
  };
}

function backtestModel(
  id: TokenDemandBacktestModel["id"],
  label: string,
  points: TokenDemandBacktestPoint[],
): TokenDemandBacktestModel {
  return {
    id,
    label,
    points,
    metrics: {
      full: metricWindow(points),
      recent6: metricWindow(points.slice(-6)),
      recent12: metricWindow(points.slice(-12)),
    },
  };
}

function makeBacktest(observations: readonly DemandObservation[]) {
  const primary: TokenDemandBacktestPoint[] = [];
  const lag1: TokenDemandBacktestPoint[] = [];
  const seasonalLag12: TokenDemandBacktestPoint[] = [];
  const drift: TokenDemandBacktestPoint[] = [];
  const priorPrimaryErrors: number[] = [];

  for (const actual of observations) {
    const history = observations.filter((row) => row.month < actual.month);
    if (history.length < 12 || history.at(-1)?.month !== previousMonth(actual.month)) continue;
    const candidates = predictCandidates(history, actual.month);
    if (candidates.seasonalLag12 === null) continue;
    const selectedPrediction = adaptivePrediction(candidates, { lag1, seasonalLag12, drift });
    const primaryInterval = intervalFor(selectedPrediction, priorPrimaryErrors, history, 1);
    const candidateEntries = [
      [primary, selectedPrediction, primaryInterval],
      [lag1, candidates.lag1, intervalFor(candidates.lag1, [], history, 1)],
      [seasonalLag12, candidates.seasonalLag12, intervalFor(candidates.seasonalLag12, [], history, 1)],
      [drift, candidates.drift, intervalFor(candidates.drift, [], history, 1)],
    ] as const;
    for (const [target, predicted, interval] of candidateEntries) {
      const error = predicted - actual.value;
      target.push({
        month: actual.month,
        horizon: 1,
        actual: actual.value,
        predicted,
        ...interval,
        error,
        absoluteError: Math.abs(error),
        absolutePercentageError: Math.abs(error / actual.value) * 100,
        intervalCovered: actual.value >= interval.low && actual.value <= interval.high,
      });
    }
    priorPrimaryErrors.push(Math.abs(selectedPrediction - actual.value));
  }
  if (!primary.length) {
    throw new Error("Monthly demand history has no calendar-contiguous rolling-origin evaluation window.");
  }
  return {
    primary: backtestModel("adaptive-selector", "Adaptive baseline selector", primary),
    lag1: backtestModel("lag-1", "Lag-1", lag1),
    seasonalLag12: backtestModel("seasonal-lag-12", "Seasonal lag-12", seasonalLag12),
    drift: backtestModel("drift", "Log drift", drift),
  };
}

function directResiduals(
  observations: readonly DemandObservation[],
  horizon: number,
  performance: Record<BaselineKey, readonly TokenDemandBacktestPoint[]>,
) {
  const residuals: number[] = [];
  for (const actual of observations) {
    const originMonth = addMonths(actual.month, -horizon);
    const history = observations.filter((row) => row.month <= originMonth);
    if (history.length < 12 || history.at(-1)?.month !== originMonth) continue;
    const prediction = predictCandidates(history, actual.month);
    if (prediction.seasonalLag12 === null) continue;
    const pointInTimePerformance = {
      lag1: performance.lag1.filter((point) => point.month <= originMonth),
      seasonalLag12: performance.seasonalLag12.filter((point) => point.month <= originMonth),
      drift: performance.drift.filter((point) => point.month <= originMonth),
    };
    const selected = adaptivePrediction(prediction, pointInTimePerformance);
    residuals.push(Math.abs(selected - actual.value));
  }
  return residuals;
}

function skillPct(primary: TokenDemandBacktestModel, baseline: TokenDemandBacktestModel) {
  const baselineRmse = baseline.metrics.full.rmse;
  return baselineRmse > 0 ? (1 - primary.metrics.full.rmse / baselineRmse) * 100 : null;
}

function normalizedScenario(options: TokenDemandOptions) {
  const demandGrowthPct = options.scenario?.demandGrowthPct ?? 0;
  const unitPriceChangePct = options.scenario?.unitPriceChangePct ?? 0;
  const priceElasticity = options.scenario?.priceElasticity ?? 0.35;
  for (const [name, value] of [
    ["demandGrowthPct", demandGrowthPct],
    ["unitPriceChangePct", unitPriceChangePct],
    ["priceElasticity", priceElasticity],
  ] as const) {
    if (!Number.isFinite(value)) throw new Error(`Scenario ${name} must be finite.`);
  }
  if (demandGrowthPct <= -100) throw new Error("Scenario demandGrowthPct must be greater than -100%.");
  if (unitPriceChangePct <= -100) throw new Error("Scenario unitPriceChangePct must be greater than -100%.");
  if (priceElasticity < 0) throw new Error("Scenario priceElasticity must be non-negative.");
  return {
    demandGrowthPct,
    unitPriceChangePct,
    priceElasticity,
    active: Math.abs(demandGrowthPct) > 1e-12 || Math.abs(unitPriceChangePct) > 1e-12,
  };
}

/**
 * Side-effect-free Token demand product slice. Input rows are supplied by the
 * ingestion seam; this module never performs network requests.
 */
export function runTokenDemandSlice(
  monthlyRows: readonly MonthlyDemandRow[],
  options: TokenDemandOptions,
): TokenDemandSliceResult {
  validateMonthlyRows(monthlyRows);
  const asOfDate = parseAsOf(options.asOf);
  const currentMonth = calendarMonth(asOfDate);
  const latestCompleteMonth = previousMonth(currentMonth);
  const completeRows = monthlyRows.filter((row) => row.month < currentMonth);
  const proxyPoints = completeRows.flatMap((row) =>
    typeof row.demandIndex === "number" && Number.isFinite(row.demandIndex)
      ? [{ month: row.month, value: row.demandIndex }]
      : [],
  );
  const platformPoints = completeRows.flatMap((row) =>
    typeof row.openRouterTokensT === "number" && Number.isFinite(row.openRouterTokensT)
      ? [{ month: row.month, value: row.openRouterTokensT }]
      : [],
  );
  const latestProxy = requiredLatest(proxyPoints, "npm/Wikimedia demand proxy");
  const latestPlatform = platformPoints.at(-1) ?? null;
  const sourceHealthMap = {
    npm: sourceHealth("npm", completeRows, latestCompleteMonth, asOfDate, (row) =>
      typeof row.sdkDownloads === "number" && Number.isFinite(row.sdkDownloads),
    ),
    wikimedia: sourceHealth("wikimedia", completeRows, latestCompleteMonth, asOfDate, (row) =>
      typeof row.wikiViews === "number" && Number.isFinite(row.wikiViews),
    ),
    demandProxy: sourceHealth("npm-wikimedia", completeRows, latestCompleteMonth, asOfDate, (row) =>
      typeof row.demandIndex === "number" && Number.isFinite(row.demandIndex),
    ),
    openrouter: sourceHealth("openrouter", completeRows, latestCompleteMonth, asOfDate, (row) =>
      typeof row.openRouterTokensT === "number" && Number.isFinite(row.openRouterTokensT),
    ),
  };
  const observations: DemandObservation[] = proxyPoints;
  const tested = makeBacktest(observations);
  const latestCandidatePerformance = {
    lag1: tested.lag1.points,
    seasonalLag12: tested.seasonalLag12.points,
    drift: tested.drift.points,
  };
  const scenario = normalizedScenario(options);
  const baselinePoints = Array.from({ length: 12 }, (_, index): TokenDemandForecastPoint => {
    const horizon = index + 1;
    const month = addMonths(latestProxy.month, horizon);
    const predicted = adaptivePrediction(predictCandidates(observations, month), latestCandidatePerformance);
    return {
      month,
      horizon,
      predicted,
      ...intervalFor(predicted, directResiduals(observations, horizon, latestCandidatePerformance), observations, horizon),
    };
  });
  const forecastPoints = baselinePoints.map((point): TokenDemandForecastPoint => {
    const growthFactor = Math.pow(1 + scenario.demandGrowthPct / 100, point.horizon / 12);
    const priceFactor = Math.pow(1 + scenario.unitPriceChangePct / 100, -scenario.priceElasticity);
    const multiplier = growthFactor * priceFactor;
    return {
      ...point,
      predicted: point.predicted * multiplier,
      low: point.low * multiplier,
      high: point.high * multiplier,
      interval: {
        ...point.interval,
        label: scenario.active
          ? `${point.interval.label}; conditional band scaled by the stated future scenario`
          : point.interval.label,
      },
    };
  });
  const openRouterObservedMonths = sourceHealthMap.openrouter.coverage.observedMonths;

  return {
    monitor: {
      asOf: asOfDate.toISOString(),
      latestCompleteMonth,
      industryDemandProxy: {
        sourceId: "npm-wikimedia",
        label: "npm/Wikimedia industry demand proxy",
        scope: "industry-demand-proxy",
        unit: "index",
        latestMonth: latestProxy.month,
        latestValue: latestProxy.value,
        points: proxyPoints,
        warning: "Adoption proxy, not a global Token count, DAU, ARR, or company revenue.",
      },
      platformTokenActual: {
        sourceId: "openrouter",
        label: "OpenRouter platform Token actual",
        scope: "single-platform",
        unit: "trillion-platform-tokens",
        latestMonth: latestPlatform?.month ?? null,
        latestValue: latestPlatform?.value ?? null,
        points: platformPoints,
        warning: "OpenRouter platform observation only; not global Token usage, DAU, ARR, or company revenue.",
      },
      modelProviderMomentum: {
        available: false,
        label: "platform-share-momentum-index",
        reason: "Aggregate OpenRouter monthly totals contain no provider-level share observations; no company revenue is inferred.",
      },
      sourceHealth: sourceHealthMap,
    },
    forecast: {
      target: "monthly-demand-index",
      unit: "index",
      baselinePoints,
      points: forecastPoints,
      horizons: {
        month1: forecastPoints[0],
        month3: forecastPoints[2],
        month12: forecastPoints[11],
      },
      openRouterChallenger: {
        role: openRouterObservedMonths < 18 ? "restricted-challenger-only" : "eligible-challenger-only",
        observedMonths: openRouterObservedMonths,
        minimumMonths: 18,
        appliedToPrimary: false,
        explanation: openRouterObservedMonths < 18
          ? "OpenRouter has fewer than 18 complete monthly observations and remains a restricted single-platform challenger."
          : "OpenRouter has enough history for a challenger audit but remains separate from the industry proxy target.",
      },
      scenarioImpact: {
        affectsCurrentChart: scenario.active,
        directTargets: ["monthly-demand-index"],
        historicalBacktestLocked: true,
        explanation: scenario.active
          ? `Future points only: annual demand growth ${scenario.demandGrowthPct}% and unit-price change ${scenario.unitPriceChangePct}% are applied directly to the monthly demand-index chart; historical backtests stay locked.`
          : "No non-zero future scenario transform is active; historical backtests stay locked.",
      },
    },
    backtest: {
      primary: tested.primary,
      baselines: {
        lag1: tested.lag1,
        seasonalLag12: tested.seasonalLag12,
        drift: tested.drift,
      },
      skillVsBaselinesPct: {
        lag1: skillPct(tested.primary, tested.lag1),
        seasonalLag12: skillPct(tested.primary, tested.seasonalLag12),
        drift: skillPct(tested.primary, tested.drift),
      },
      historicalScenarioLocked: true,
    },
    methodology: {
      targetDefinition: "Monthly npm/Wikimedia geometric-mean adoption index; not global Token volume, DAU, ARR, or revenue.",
      formula: "argmin recent-12M OOS RMSE among lag-1, seasonal lag-12, and positive log drift; robust median until six prior errors exist",
      estimator: "Deterministic calendar-aware adaptive selector; selection at each origin uses only earlier baseline errors.",
      featureTiming: "Each rolling-origin prediction uses only complete UTC months visible at its forecast origin.",
      validation: "Expanding-window rolling-origin backtest on identical calendar-contiguous targets for the model and all three baselines; full, recent-6M, and recent-12M metrics.",
      interval: "Horizon-specific 90% OOS conformal absolute-error quantiles after nine genuine OOS residuals; otherwise an explicitly uncalibrated RMSE fallback band.",
      scenarioTransform: "Demand growth and price assumptions transform only future points; historical rolling-origin predictions never change.",
      minimumTrainingMonths: 12,
      caveats: [
        "npm downloads can include bots and package migration; Wikimedia interest is not usage.",
        "OpenRouter is one routing platform and never supplies a global Token denominator.",
        "Aggregate platform totals cannot establish model-provider revenue or market share.",
      ],
    },
    sources: [
      { id: "npm", role: "SDK adoption proxy", scope: "public platform download counts" },
      { id: "wikimedia", role: "public interest proxy", scope: "public page views" },
      { id: "openrouter", role: "restricted Token actual / challenger", scope: "OpenRouter platform only" },
    ],
  };
}
